import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { paymentsAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function PagoSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await paymentsAPI.getStripeStatus(sessionId);
      const data = response.data;
      setPaymentData(data);

      if (data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="pago-success-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-heading">Estado del Pago</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-blue-600 animate-spin" />
              <p className="text-slate-600">Verificando pago...</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto text-green-600" />
              <div>
                <h2 className="text-xl font-bold text-green-600">¡Pago Exitoso!</h2>
                <p className="text-slate-600 mt-2">Tu pago ha sido procesado correctamente.</p>
                {paymentData && (
                  <p className="text-lg font-medium mt-4">
                    Monto: ${(paymentData.amount_total / 100).toLocaleString("es-MX")} {paymentData.currency?.toUpperCase()}
                  </p>
                )}
              </div>
            </>
          )}

          {status === "expired" && (
            <>
              <XCircle className="w-16 h-16 mx-auto text-amber-600" />
              <div>
                <h2 className="text-xl font-bold text-amber-600">Sesión Expirada</h2>
                <p className="text-slate-600 mt-2">La sesión de pago ha expirado. Por favor intenta de nuevo.</p>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="w-16 h-16 mx-auto text-red-600" />
              <div>
                <h2 className="text-xl font-bold text-red-600">Error</h2>
                <p className="text-slate-600 mt-2">Hubo un error al verificar el pago.</p>
              </div>
            </>
          )}

          {status === "timeout" && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-amber-600" />
              <div>
                <h2 className="text-xl font-bold text-amber-600">Verificación en Proceso</h2>
                <p className="text-slate-600 mt-2">
                  El pago está siendo procesado. Recibirás confirmación por email.
                </p>
              </div>
            </>
          )}

          <div className="pt-4">
            <Link to="/dashboard">
              <Button className="w-full" data-testid="back-to-dashboard-btn">
                Volver al Dashboard
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
